import type { App } from 'vue';
import type { Router } from 'vue-router';
import { usePB } from '@/composables/database/use-pocket-base.ts';
import { generateUuid } from '@/utils/generate-uuid.ts';

export type ClientDiagnosticLevel = 'warning' | 'error';
export type ClientDiagnosticSource = 'console' | 'vue' | 'router' | 'window';

export type ClientDiagnosticPayload = {
  session_id: string;
  level: ClientDiagnosticLevel;
  source: ClientDiagnosticSource;
  message: string;
  stack: string;
  route: string;
  release: string;
  browser: string;
  platform: string;
  fingerprint: string;
};

type Reporter = (payload: ClientDiagnosticPayload) => Promise<unknown>;
type DiagnosticInput = {
  level: ClientDiagnosticLevel;
  source: ClientDiagnosticSource;
  value: unknown;
  detail?: unknown;
};

const MAX_TEXT_LENGTH = 2_000;
const MAX_STACK_LENGTH = 8_000;
const DEDUPE_WINDOW_MS = 60_000;
const MAX_QUEUE_LENGTH = 50;
const REDACTED = '[redacted]';
const ignoredWarning = /(toast|vue-toastification)/i;
const authorization = /["']?authorization["']?\s*[:=]\s*["']?\s*(?:(?:basic|bearer)\s+)?[a-z0-9._~+/=-]+/gi;
const secret = /(["']?(?:cookie|credential|password|passwd|secret|token|api[-_]?key|session)["']?)(\s*[:=]\s*)["']?([^\s,;"'}]+)/gi;
const email = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const urlCredentials = /(https?:\/\/)([^/\s:@]+):([^/\s@]+)@/gi;
const queryOrHash = /([?#])[^)\]}\s]+/g;

function truncate(value: string, length: number) {
  return value.length <= length ? value : `${value.slice(0, length - 1)}…`;
}

export function redactDiagnosticText(value: string, maxLength = MAX_TEXT_LENGTH) {
  return truncate(
    value
      .replace(urlCredentials, `$1${REDACTED}@`)
      .replace(queryOrHash, '$1[redacted]')
      .replace(authorization, `authorization=${REDACTED}`)
      .replace(email, REDACTED)
      .replace(secret, `$1$2${REDACTED}`),
    maxLength,
  );
}

export function normalizeDiagnosticValue(value: unknown) {
  try {
    if (value instanceof Error) return redactDiagnosticText(`${value.name}: ${value.message}`);
  } catch {
    return '[Object]';
  }
  if (typeof value === 'string') return redactDiagnosticText(value);
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'symbol') return value.description ? `Symbol(${redactDiagnosticText(value.description)})` : 'Symbol';
  if (typeof value === 'function') return `[Function${value.name ? ` ${redactDiagnosticText(value.name)}` : ''}]`;
  try {
    const name = Object.getPrototypeOf(value)?.constructor?.name;
    return `[${typeof name === 'string' && name ? redactDiagnosticText(name) : 'Object'}]`;
  } catch {
    return '[Object]';
  }
}

function normalizedStack(value: unknown, detail?: unknown) {
  try {
    const stack = value instanceof Error ? value.stack : detail instanceof Error ? detail.stack : '';
    return stack ? redactDiagnosticText(stack, MAX_STACK_LENGTH) : '';
  } catch {
    return '';
  }
}

function currentRoute() {
  return redactDiagnosticText(window.location.pathname, 500);
}

function browserFamily() {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
}

function platformFamily() {
  const value = navigator.userAgent;
  if (/Android/i.test(value)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(value)) return 'iOS';
  if (/Windows/i.test(value)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(value)) return 'macOS';
  if (/Linux/i.test(value)) return 'Linux';
  return 'Other';
}

function releaseName() {
  return document.querySelector<HTMLMetaElement>('meta[name="vennevent-build"]')?.content.trim() || 'unknown';
}

export function diagnosticFingerprint(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function isExpectedFailure(value: unknown) {
  try {
    if (!navigator.onLine) return true;
    if (value instanceof DOMException && value.name === 'AbortError') return true;
    if (value instanceof Error && value.name === 'AbortError') return true;
    return false;
  } catch {
    return false;
  }
}

export function createClientDiagnosticCollector(reporter: Reporter, now: () => number = Date.now) {
  const sessionId = generateUuid();
  const recent = new Map<string, number>();
  const queue: ClientDiagnosticPayload[] = [];
  let sending = false;

  async function flush() {
    if (sending) return;
    sending = true;
    while (queue.length) {
      const payload = queue.shift();
      if (!payload) continue;
      try {
        await reporter(payload);
      } catch {
        // Diagnostics must never create another user-visible or captured failure.
      }
    }
    sending = false;
  }

  function capture(input: DiagnosticInput) {
    try {
      if (isExpectedFailure(input.value)) return;
      const message = [normalizeDiagnosticValue(input.value), input.detail === undefined ? '' : normalizeDiagnosticValue(input.detail)]
        .filter(Boolean)
        .join(' ');
      if (!message || (input.level === 'warning' && ignoredWarning.test(message))) return;
      const route = currentRoute();
      const fingerprintSource = `${input.level}|${input.source}|${message}|${route}`;
      const fingerprint = diagnosticFingerprint(fingerprintSource);
      const timestamp = now();
      const previous = recent.get(fingerprint);
      if (previous !== undefined && timestamp - previous < DEDUPE_WINDOW_MS) return;
      recent.set(fingerprint, timestamp);
      for (const [key, seenAt] of recent) if (timestamp - seenAt >= DEDUPE_WINDOW_MS) recent.delete(key);
      if (queue.length >= MAX_QUEUE_LENGTH) queue.shift();
      queue.push({
        session_id: sessionId,
        level: input.level,
        source: input.source,
        message,
        stack: normalizedStack(input.value, input.detail),
        route,
        release: releaseName(),
        browser: browserFamily(),
        platform: platformFamily(),
        fingerprint,
      });
      void flush();
    } catch {
      // Diagnostics must never alter application behavior.
    }
  }

  return { capture };
}

export function installClientDiagnostics(app: App, router: Router) {
  const pb = usePB();
  const collector = createClientDiagnosticCollector((payload) =>
    pb.db.send('/api/custom/client-diagnostics', {
      method: 'POST',
      body: payload,
      requestKey: null,
    }),
  );

  const originalWarn = console.warn;
  const originalError = console.error;
  console.warn = (...values: unknown[]) => {
    originalWarn.apply(console, values);
    collector.capture({ level: 'warning', source: 'console', value: values[0], detail: values[1] });
  };
  console.error = (...values: unknown[]) => {
    originalError.apply(console, values);
    collector.capture({ level: 'error', source: 'console', value: values[0], detail: values[1] });
  };

  const previousErrorHandler = app.config.errorHandler;
  const previousWarnHandler = app.config.warnHandler;
  app.config.errorHandler = (error, instance, info) => {
    collector.capture({ level: 'error', source: 'vue', value: error, detail: info });
    previousErrorHandler?.(error, instance, info);
  };
  app.config.warnHandler = (message, instance, trace) => {
    collector.capture({ level: 'warning', source: 'vue', value: message, detail: trace });
    previousWarnHandler?.(message, instance, trace);
  };

  router.onError((error) => collector.capture({ level: 'error', source: 'router', value: error }));
  window.addEventListener('error', (event) => collector.capture({ level: 'error', source: 'window', value: event.error ?? event.message }));
  window.addEventListener('unhandledrejection', (event) =>
    collector.capture({ level: 'error', source: 'window', value: event.reason ?? 'Unhandled promise rejection' }),
  );
}
